import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { getLaboklinApi } from '../../../services/laboklinApi';
import { LABOKLIN_BORDER } from '../../../theme/laboklinTheme';
import {
  Assessment,
  AssessmentRating,
  AssessmentSection,
  RATING_LABELS,
  RunStep,
} from '../../../types/labConsistency';

/**
 * Where the veterinarian answers the question the machine cannot.
 *
 * Everything above this is agreement between runs, which says only that the answer is
 * the same each time. Whether a consistent answer is a GOOD one is a clinical
 * judgement, and it is the whole reason the report exists.
 */

interface SectionAssessmentProps {
  batchId: string;
  reportNo: string;
  step: RunStep;
  section: AssessmentSection;
  contentHash: string;
  existing?: Assessment;
}

type SaveState = 'pristine' | 'dirty' | 'saving' | 'saved' | 'error';

const draftKey = (p: SectionAssessmentProps) =>
  `consistency-draft|${p.batchId}|${p.reportNo}|${p.step}|${p.section}`;

export const SectionAssessment: React.FC<SectionAssessmentProps> = (props) => {
  const { batchId, reportNo, step, section, contentHash, existing } = props;

  const key = draftKey(props);
  const [rating, setRating] = useState<AssessmentRating | null>(existing?.rating ?? null);
  const [comment, setComment] = useState(existing?.comment ?? '');
  const [state, setState] = useState<SaveState>('pristine');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(existing?.updatedAt ?? null);

  // A mis-click must not lose a half-written clinical judgement. Per-viewer and
  // per-section, so two sections cannot overwrite each other's draft.
  useEffect(() => {
    if (state !== 'dirty') return;
    try {
      sessionStorage.setItem(key, JSON.stringify({ rating, comment }));
    } catch {
      // Private windows and blocked site data throw here; a lost draft is survivable.
    }
  }, [key, rating, comment, state]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const draft = JSON.parse(raw) as { rating: AssessmentRating | null; comment: string };
      if (draft.comment !== (existing?.comment ?? '') || draft.rating !== (existing?.rating ?? null)) {
        setRating(draft.rating);
        setComment(draft.comment);
        setState('dirty');
      }
    } catch {
      // Ignore an unreadable draft rather than blocking the form on it.
    }
    // Only on mount, and only for this section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!rating) return;
    setState('saving');
    setError(null);
    try {
      const { assessment } = await getLaboklinApi().saveConsistencyAssessment({
        batchId,
        reportNo,
        step,
        section,
        rating,
        comment,
      });
      setSavedAt(assessment.updatedAt);
      setState('saved');
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* nothing to clean up */
      }
    } catch (err) {
      // The text stays in the field. Losing a written judgement to a failed request
      // would be worse than the failure.
      setError(err instanceof Error ? err.message : String(err));
      setState('error');
    }
  };

  const staleAssessment = existing && existing.assessedContentHash !== contentHash;

  return (
    <Box sx={{ mt: 2, pt: 1.75, borderTop: `1px solid ${LABOKLIN_BORDER}`, bgcolor: 'rgba(0,0,0,0.015)', px: 1.5, py: 1.5, borderRadius: 1 }}>
      <Typography
        sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, mb: 1 }}
      >
        Your clinical assessment
      </Typography>

      {staleAssessment && (
        <Alert severity="warning" sx={{ mb: 1.25, fontSize: 12, py: 0 }}>
          This was assessed against an earlier version of this report.
        </Alert>
      )}

      <ToggleButtonGroup
        exclusive
        size="small"
        value={rating}
        onChange={(_, next: AssessmentRating | null) => {
          if (!next) return;
          setRating(next);
          setState('dirty');
        }}
        sx={{ mb: 1.25, flexWrap: 'wrap' }}
      >
        {(Object.keys(RATING_LABELS) as AssessmentRating[]).map((value) => (
          <ToggleButton key={value} value={value} sx={{ fontSize: 12, textTransform: 'none' }}>
            {RATING_LABELS[value]}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        value={comment}
        placeholder="Does this make sense clinically? What would you have expected?"
        onChange={(e) => {
          setComment(e.target.value);
          setState('dirty');
        }}
        inputProps={{ maxLength: 4000 }}
        sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 13 } }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 1, fontSize: 12, py: 0 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* No autosave. A half-typed clinical judgement persisted and later read back
            as a verdict is worse than asking for a click. */}
        <Button
          variant="contained"
          size="small"
          disabled={!rating || state === 'saving' || state === 'pristine' || state === 'saved'}
          onClick={save}
          sx={{ textTransform: 'none' }}
        >
          {state === 'saving' ? 'Saving…' : 'Save'}
        </Button>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {state === 'dirty' && 'Unsaved changes'}
          {state === 'saved' && 'Saved just now'}
          {state === 'error' && 'Not saved — your text is still here'}
          {state === 'pristine' &&
            (savedAt ? `Saved ${new Date(savedAt).toLocaleString()}` : 'Not yet assessed')}
        </Typography>
      </Box>
    </Box>
  );
};

export default SectionAssessment;
